import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import restaurantService, {
  CatalogItem,
  CustomerDisplaySettings,
  MenuItem,
  MenuRecommendationsPayload,
  PlanFeaturesPayload,
  Restaurant,
  RestaurantCustomerReview,
  RestaurantCustomerReviewSummary,
} from '../../services/restaurantService';
import CustomerReviewList from '../RestaurantReviews/CustomerReviewList';
import { HOME_REVIEWS_PREVIEW_LIMIT } from '../../constants/customerReviews';
import '../RestaurantReviews/CustomerReviewList.css';
import '../Footer/footerIcons.css';
import { getRestaurantDisplayRating } from '../../utils/restaurantRating';
import {
  CUSTOMER_FEATURE_DINE_IN,
  CUSTOMER_FEATURE_TABLE_BOOKING,
  CUSTOMER_FEATURE_TAKEAWAY,
  hasCustomerFeature,
  isGuestDeliveryOpen,
} from '../../utils/planFeatures';
import { guestOrderTypeToService, getStoredGuestOrderType, resolveGuestOrderType, saveGuestOrderType } from '../../utils/guestOrderTypeStorage';
import deliveryService, { DeliveryConfig } from '../../services/deliveryService';
import { useAppSelector } from '../../store/hooks';
import cartService from '../../services/cartService';
import { DEFAULT_IMAGES, getRestaurantImageUrl } from '../../constants/images';
import MenuItemThumbnail, { resolveMenuItemImageSrc } from '../common/MenuItemThumbnail';
import { getDisplayMrp, getDisplayPrice, getEffectiveDisplayPrice } from '../../utils/menuPricing';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import RestaurantHomeSkeleton from './RestaurantHomeSkeleton';
import {
  IconBag,
  IconBike,
  IconCalendar,
  IconCamera,
  IconChevronRight,
  IconFlame,
  IconGift,
  IconMapPin,
  IconMenuItems,
  IconMenuList,
  IconNavigation,
  IconOffers,
  IconPhone,
  IconPinCard,
  IconShare,
  IconTag,
  IconUser,
  IconUtensils,
  IconWallet,
  IconCalendarSmall,
} from './RestaurantDetailIcons';
import { FooterRatingStars } from '../Footer/FooterIcons';
import {
  getPlatformReviewLinks,
  getSocialMediaLinks,
  hasSocialLinks,
} from '../../utils/restaurantSocialLinks';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import SeoHead from '../Seo/SeoHead';
import {
  buildRestaurantPageTitle,
  buildRestaurantPageDescription,
  shouldIndexRestaurantPage,
} from '../../utils/seoMetadata';
import './RestaurantHome.css';
import CatalogCategoryGrid from '../Catalog/CatalogCategoryGrid';
import CatalogCuisineSlider from '../Catalog/CatalogCuisineSlider';
import OffersDrawer from '../common/OffersDrawer/OffersDrawer';
import { useEnabledGames } from '../Games/useEnabledGames';
import { formatCouponBadge, formatCouponText } from '../../utils/offerFormatting';
import GuestOrderingBanner from '../common/GuestOrderingBanner/GuestOrderingBanner';
import { GuestOrderingStatus } from '../../utils/guestOrdering';

interface OpeningHours {
  day: string;
  breakfast?: string;
  lunch?: string;
  dinner?: string;
}

const RestaurantHome: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const { hasEnabledGames } = useEnabledGames(appName);
  const preload = useSsrPreload();
  const landingPreload = matchSsrPreload(preload, 'restaurant', appName) ? preload : null;
  const [restaurant, setRestaurant] = useState<Restaurant | null>(landingPreload?.restaurant ?? null);
  const [openingHours, setOpeningHours] = useState<OpeningHours[]>(landingPreload?.openingHours ?? []);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(landingPreload?.menuItems ?? []);
  const [promotions, setPromotions] = useState<Array<{ id: number; name: string; description?: string; discountPreview?: string }>>([]);
  const [publicCoupons, setPublicCoupons] = useState<
    Array<{ id: number; couponCode?: string; couponType?: string; discount?: number; minAmount?: number; description?: string }>
  >([]);
  const [loading, setLoading] = useState(!landingPreload?.restaurant);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(landingPreload?.guestOrdering ?? null);
  const [displaySettings, setDisplaySettings] = useState<CustomerDisplaySettings | null>(landingPreload?.displaySettings ?? null);
  const [activeService, setActiveService] = useState<'dine' | 'takeaway' | 'delivery' | 'reserve' | null>(null);
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(landingPreload?.planFeatures);
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig | null>(null);
  const [deliveryConfigReady, setDeliveryConfigReady] = useState(false);
  const [catalogCategories, setCatalogCategories] = useState<CatalogItem[]>(landingPreload?.catalogCategories ?? []);
  const [catalogCuisines, setCatalogCuisines] = useState<CatalogItem[]>(landingPreload?.catalogCuisines ?? []);
  const [menuRecommendations, setMenuRecommendations] = useState<MenuRecommendationsPayload | null>(
    landingPreload?.menuRecommendations ?? null
  );
  const [orderSticky, setOrderSticky] = useState(false);
  const [offersDrawerOpen, setOffersDrawerOpen] = useState(false);
  const [customerReviews, setCustomerReviews] = useState<RestaurantCustomerReview[]>(
    landingPreload?.customerReviews ?? []
  );
  const [customerReviewSummary, setCustomerReviewSummary] =
    useState<RestaurantCustomerReviewSummary | null>(landingPreload?.customerReviewSummary ?? null);
  const orderStripSentinelRef = useRef<HTMLDivElement>(null);
  const customerReviewsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    if (appName && !landingPreload?.restaurant) {
      loadPageData();
    }
  }, [appName]);

  useEffect(() => {
    if (!appName) {
      return;
    }
    let cancelled = false;
    deliveryService
      .getConfig(appName)
      .then((cfg) => {
        if (!cancelled) {
          setDeliveryConfig(cfg);
          setDeliveryConfigReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeliveryConfig(null);
          setDeliveryConfigReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appName]);

  useEffect(() => {
    if (openingHours.length > 0) {
      checkRestaurantStatus();
      const interval = setInterval(checkRestaurantStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [openingHours]);

  useEffect(() => {
    const sentinel = orderStripSentinelRef.current;
    if (!sentinel || loading) return;

    const updateSticky = () => {
      const headerVar = getComputedStyle(document.documentElement).getPropertyValue('--app-header-height');
      const headerHeight = parseInt(headerVar, 10) || 56;
      const { top } = sentinel.getBoundingClientRect();
      setOrderSticky(top < headerHeight);
    };

    updateSticky();
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);
    return () => {
      window.removeEventListener('scroll', updateSticky);
      window.removeEventListener('resize', updateSticky);
    };
  }, [loading, restaurant?.id, deliveryConfig]);

  useEffect(() => {
    const slides = restaurant?.restaurantLandingDetailsList;
    if (slides && slides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [restaurant]);

  const loadPageData = async () => {
    if (!appName) return;
    setLoading(true);
    setError(null);
    try {
      const [restRes, menuRes, promoRes, couponRes, catalogRes, recRes, reviewsRes] = await Promise.all([
        restaurantService.getRestaurant(appName),
        restaurantService.getRestaurantMenu(appName),
        cartService.getActivePromotions(appName),
        restaurantService.getPublicCoupons(appName),
        restaurantService.getRestaurantCatalog(appName),
        restaurantService.getMenuRecommendations(appName, undefined, 5),
        restaurantService.getRestaurantReviews(appName, HOME_REVIEWS_PREVIEW_LIMIT, 0),
      ]);

      if (restRes.success && restRes.restaurant) {
        setRestaurant(restRes.restaurant);
        setOpeningHours(restRes.openingHours || []);
        setPlanFeatures(restRes.features);
        setGuestOrdering(restRes.guestOrdering ?? null);
        setDisplaySettings(restRes.displaySettings ?? null);
        setCustomerReviewSummary(restRes.restaurant.customerReviews ?? null);
      } else {
        setError(restRes.message || 'Restaurant not found');
        return;
      }

      if (menuRes.success && menuRes.menu?.menuList) {
        setMenuItems(menuRes.menu.menuList);
      }

      if (promoRes.success) {
        setPromotions(promoRes.promotions || []);
      }
      if (couponRes.success) {
        setPublicCoupons(couponRes.coupons || []);
      }

      if (catalogRes.success) {
        setCatalogCategories(catalogRes.categories || []);
        setCatalogCuisines(catalogRes.cuisines || []);
      }
      if (recRes.success && recRes.recommendations) {
        setMenuRecommendations(recRes.recommendations);
      }
      if (reviewsRes.success) {
        setCustomerReviews(reviewsRes.reviews || []);
        if (reviewsRes.summary) {
          setCustomerReviewSummary(reviewsRes.summary);
        }
      } else {
        setCustomerReviews([]);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'An error occurred while loading restaurant');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToMenu = (orderType?: number) => {
    if (appName && orderType != null) {
      saveGuestOrderType(appName, orderType);
    }
    const query = orderType != null ? `?type=${orderType}` : '';
    navigate(`/restaurants/${appName}/menu${query}`);
  };
  const handleBrowseMenu = () => {
    const selectedType =
      activeService === 'dine' ? 1 : activeService === 'delivery' ? 2 : activeService === 'takeaway' ? 3 : undefined;
    handleNavigateToMenu(selectedType);
  };
  const handleBrowseCategory = (item: CatalogItem) =>
    navigate(`/restaurants/${appName}/browse?category=${item.id}`);
  const handleBrowseCuisine = (item: CatalogItem) =>
    navigate(`/restaurants/${appName}/browse?cuisine=${item.id}`);
  const handleNavigateToReservation = () => {
    if (!hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TABLE_BOOKING)) {
      return;
    }
    navigate(`/restaurants/${appName}/reserve-table`);
  };

  const tableBookingEnabled = hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TABLE_BOOKING);
  const dineInEnabled = hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_DINE_IN);
  const takeawayEnabled = hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TAKEAWAY);
  const deliveryEnabled = isGuestDeliveryOpen(planFeatures, deliveryConfig);
  const hasOrderModes = dineInEnabled || takeawayEnabled || deliveryEnabled || tableBookingEnabled;

  useEffect(() => {
    if (!appName || !hasOrderModes) {
      return;
    }
    const storedType = getStoredGuestOrderType(appName);
    if (storedType === 2 && !deliveryConfigReady) {
      return;
    }
    const stored = guestOrderTypeToService(
      resolveGuestOrderType({
        appName,
        features: planFeatures,
        deliveryConfig,
      })
    );
    if (stored === 'dine' && dineInEnabled) {
      setActiveService('dine');
    } else if (stored === 'takeaway' && takeawayEnabled) {
      setActiveService('takeaway');
    } else if (stored === 'delivery' && deliveryEnabled) {
      setActiveService('delivery');
    }
  }, [
    appName,
    hasOrderModes,
    dineInEnabled,
    takeawayEnabled,
    deliveryEnabled,
    planFeatures,
    deliveryConfig,
    deliveryConfigReady,
  ]);

  const getFullAddress = useCallback(() => {
    if (!restaurant?.restaurantAddressDetails) return 'Address not available.';
    const address = restaurant.restaurantAddressDetails;
    const parts = [
      address.locality,
      address.landmark,
      address.area,
      address.city,
      address.state,
      address.pincode,
      address.country,
    ].filter((part) => part != null && String(part).trim() !== '');
    return parts.join(', ') || 'Address not available.';
  }, [restaurant]);

  const hasMapCoordinates = useCallback(() => {
    const address = restaurant?.restaurantAddressDetails;
    if (!address) return false;
    const { lat, lng } = address;
    return lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  }, [restaurant]);

  const getDirectionsUrl = useCallback(() => {
    const address = restaurant?.restaurantAddressDetails;
    if (!address) return null;
    if (address.googleMapLink?.trim()) return address.googleMapLink.trim();
    if (hasMapCoordinates()) {
      return `https://www.google.com/maps/dir/?api=1&destination=${address.lat},${address.lng}`;
    }
    const full = getFullAddress();
    if (full && full !== 'Address not available.') {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(full)}`;
    }
    return null;
  }, [restaurant, getFullAddress, hasMapCoordinates]);

  const checkRestaurantStatus = () => {
    if (openingHours.length === 0) {
      setIsOpen(null);
      return;
    }
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const todayHours = openingHours.find((h) => h.day.toLowerCase() === currentDay.toLowerCase());
    if (!todayHours) {
      setIsOpen(false);
      return;
    }

    const timeToMinutes = (timeStr: string): number | null => {
      if (!timeStr) return null;
      let time = timeStr.trim();
      let isPM = false;
      if (/PM|pm/.test(time)) {
        isPM = true;
        time = time.replace(/PM|pm/gi, '').trim();
      } else {
        time = time.replace(/AM|am/gi, '').trim();
      }
      const timePart = time.split(/[\s-]/)[0];
      const [hoursStr, minutesStr] = timePart.split(':');
      if (!hoursStr) return null;
      let hours = parseInt(hoursStr, 10);
      const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      if (isPM && hours !== 12) hours += 12;
      else if (!isPM && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    let openNow = false;
    const slots = [todayHours.breakfast, todayHours.lunch, todayHours.dinner];
    for (const slot of slots) {
      if (!slot || slot.toLowerCase() === 'closed') continue;
      const parts = slot.split(/[-–—]/).map((t) => t.trim());
      if (parts.length === 2) {
        const start = timeToMinutes(parts[0]);
        const end = timeToMinutes(parts[1]);
        if (start != null && end != null && currentMinutes >= start && currentMinutes <= end) {
          openNow = true;
          break;
        }
      }
    }
    setIsOpen(openNow);
  };

  const currencySymbol = restaurant?.currencySymbol || '₹';

  const formatPrice = (amount: number) => `${currencySymbol}${amount}`;

  const formatAvgCostForTwo = useCallback(
    (raw?: string): string => {
      if (!raw?.trim()) return '';
      const t = raw.trim();
      if (/per\s+pax|per\s+person|\/\s*pax/i.test(t)) return t;
      if (/^\d+(\.\d+)?$/.test(t)) {
        return `${currencySymbol}${t} per pax`;
      }
      if (/^[₹$]?\d/.test(t) && !/per\s+pax|per\s+person/i.test(t)) {
        return `${t} per pax`;
      }
      return t;
    },
    [currencySymbol]
  );

  const avgCostLabel = formatAvgCostForTwo(restaurant?.avgCostForTwo);

  const establishedLabel = useMemo(() => {
    const y = restaurant?.establishedYear?.trim();
    if (!y) return null;
    return /^est\.?\s/i.test(y) ? y : `Est. ${y}`;
  }, [restaurant?.establishedYear]);

  const todaysSpecials = useMemo(() => {
    if (menuRecommendations?.todaysSpecial?.length) {
      return menuRecommendations.todaysSpecial;
    }
    return menuItems.filter((m) => m.specialItem && m.availability !== false).slice(0, 5);
  }, [menuRecommendations, menuItems]);

  const bestSellingItems = useMemo(
    () => menuRecommendations?.bestSelling ?? [],
    [menuRecommendations]
  );

  const showImages = displaySettings?.showMenuItemImages === true;

  const renderPopularDishList = (items: MenuItem[]) => {
    if (!restaurant || items.length === 0) {
      return <p className="rd-empty-specials">No items to show right now. Browse the full menu.</p>;
    }
    return items.map((item) => {
      const price = getEffectiveDisplayPrice(item);
      const mrp = getDisplayMrp(item);
      const baseSelling = getDisplayPrice(item);
      const comparePrice = mrp != null && mrp > baseSelling ? mrp : baseSelling;
      const pct =
        comparePrice > price
          ? Math.round(((comparePrice - price) / comparePrice) * 100)
          : item.discountPercent;
      return (
        <button
          key={item.id}
          type="button"
          className={`rd-popular-dish${showImages ? '' : ' rd-popular-dish--no-image'}`}
          onClick={() => item.itemUrl && navigate(`/restaurants/${appName}/menu/${item.itemUrl}`)}
        >
          {showImages && (
            <MenuItemThumbnail
              restroId={restaurant.id}
              menuId={item.id}
              filename={item.imageBig}
              className="rd-popular-dish-thumb menu-item-thumb"
              imgClassName="rd-popular-dish-img"
            />
          )}
          <div className="rd-popular-dish-info">
            <div className="rd-popular-dish-name">{item.item}</div>
            <div className="rd-popular-dish-prices">
              <span className="rd-popular-dish-price">{formatPrice(price)}</span>
              {comparePrice > price && (
                <span className="rd-popular-dish-orig">{formatPrice(comparePrice)}</span>
              )}
              {pct != null && pct > 0 && <span className="rd-popular-dish-discount">{pct}%</span>}
            </div>
          </div>
        </button>
      );
    });
  };

  const galleryImages = useMemo(() => {
    if (!restaurant) return [];
    const seen = new Set<string>();
    const urls: string[] = [];
    const add = (url: string) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      urls.push(url);
    };
    menuItems.forEach((item) => {
      const url = resolveMenuItemImageSrc(restaurant.id, item.id, item.imageBig);
      if (url !== DEFAULT_IMAGES.MENU_ITEM) {
        add(url);
      }
    });
    if (restaurant.coverImage) add(restaurant.coverImage);
    return urls;
  }, [menuItems, restaurant]);

  const heroSlides = useMemo(() => {
    const landing = restaurant?.restaurantLandingDetailsList || [];
    if (landing.length > 0) return landing.map((l) => l.image);
    if (galleryImages.length > 0) return galleryImages.slice(0, 5);
    if (restaurant?.coverImage) return [restaurant.coverImage];
    if (restaurant) return [getRestaurantImageUrl(restaurant.id, 'default')];
    return [DEFAULT_IMAGES.RESTAURANT];
  }, [restaurant, galleryImages]);

  const offersCount = promotions.length + publicCoupons.length;

  const openOffersDrawer = useCallback(() => {
    if (offersCount > 0) {
      setOffersDrawerOpen(true);
    }
  }, [offersCount]);

  const platformReviewLinks = useMemo(
    () => getPlatformReviewLinks(restaurant?.socialLinks),
    [restaurant?.socialLinks]
  );
  const socialMediaLinks = useMemo(
    () => getSocialMediaLinks(restaurant?.socialLinks),
    [restaurant?.socialLinks]
  );
  const showOnlinePresence = hasSocialLinks(restaurant?.socialLinks);

  const pepparAverageRating =
    customerReviewSummary?.averageRating ?? restaurant?.pepparRating ?? null;
  const pepparReviewCount = customerReviewSummary?.reviewCount ?? customerReviews.length;
  const displayListingRating = restaurant ? getRestaurantDisplayRating(restaurant) : null;
  const heroRating =
    pepparAverageRating != null && pepparAverageRating > 0
      ? pepparAverageRating
      : displayListingRating;

  const scrollToCustomerReviews = useCallback(() => {
    const el = customerReviewsRef.current;
    if (!el) return;
    const headerVar = getComputedStyle(document.documentElement).getPropertyValue('--app-header-height');
    const headerHeight = parseInt(headerVar, 10) || 56;
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    const title = restaurant?.restaurantName || 'Restaurant';
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  if (loading) {
    return <RestaurantHomeSkeleton />;
  }

  if (error || !restaurant) {
    return (
      <div className="rd-page">
        <Header />
        <div className="rd-error-wrap">
          <p className="rd-error">{error || 'Restaurant not found'}</p>
        </div>
        <Footer variant="restaurant" restaurant={restaurant} openingHours={openingHours} />
      </div>
    );
  }

  const directionsUrl = getDirectionsUrl();
  const showEmbeddedMap = hasMapCoordinates();

  const renderOrderModeButtons = (compact = false) => {
    if (!hasOrderModes) {
      return null;
    }
    const iconSize = compact ? 22 : 28;
    return (
      <div
        className={`rd-service-grid${compact ? ' rd-service-grid--compact' : ''}`}
        role="group"
        aria-label="Order options"
      >
        {dineInEnabled && (
          <button
            type="button"
            className={`rd-service-card ${activeService === 'dine' ? 'active' : ''}`}
            onClick={() => {
              setActiveService('dine');
              handleNavigateToMenu(1);
            }}
            aria-label="Dine In"
          >
            <IconUtensils size={iconSize} className="rd-service-icon" />
            <span className="rd-service-name">Dine In</span>
          </button>
        )}
        {takeawayEnabled && (
          <button
            type="button"
            className={`rd-service-card ${activeService === 'takeaway' ? 'active' : ''}`}
            onClick={() => {
              setActiveService('takeaway');
              handleNavigateToMenu(3);
            }}
            aria-label="Take Away"
          >
            <IconBag size={iconSize} className="rd-service-icon" />
            <span className="rd-service-name">Take Away</span>
          </button>
        )}
        {deliveryEnabled && (
          <button
            type="button"
            className={`rd-service-card ${activeService === 'delivery' ? 'active' : ''}`}
            onClick={() => {
              setActiveService('delivery');
              handleNavigateToMenu(2);
            }}
            aria-label="Delivery"
          >
            <IconBike size={iconSize} className="rd-service-icon" />
            <span className="rd-service-name">Delivery</span>
          </button>
        )}
        {tableBookingEnabled && (
          <button
            type="button"
            className={`rd-service-card ${activeService === 'reserve' ? 'active' : ''}`}
            onClick={() => {
              setActiveService('reserve');
              handleNavigateToReservation();
            }}
            aria-label={compact ? 'Reserve' : 'Reserve Table'}
          >
            <IconCalendar size={iconSize} className="rd-service-icon" />
            <span className="rd-service-name">{compact ? 'Reserve' : 'Reserve Table'}</span>
          </button>
        )}
      </div>
    );
  };

  const renderHeroRatingButton = (className: string) =>
    heroRating != null && heroRating > 0 ? (
      <button
        type="button"
        className={className}
        onClick={scrollToCustomerReviews}
        aria-label={`Rating ${heroRating.toFixed(1)} out of 5. View customer reviews`}
      >
        <i className="fa fa-star" aria-hidden />
        <span className="rd-hero-rating-val">{heroRating.toFixed(1)}</span>
        {pepparReviewCount > 0 && (
          <span className="rd-hero-rating-count">({pepparReviewCount})</span>
        )}
      </button>
    ) : null;

  const renderOrderModesCard = () =>
    hasOrderModes ? (
    <div className="rd-card rd-order-modes-card">
      <div className="rd-card-header">
        <div className="rd-card-title">
          <span className="rd-card-title-icon">
            <IconUtensils size={16} />
          </span>
          How would you like to order?
        </div>
      </div>
      <div className="rd-card-body">{renderOrderModeButtons(false)}</div>
    </div>
    ) : null;

  return (
    <div className={`rd-page${orderSticky ? ' rd-page--order-sticky' : ''}`}>
      <SeoHead
        title={buildRestaurantPageTitle(restaurant)}
        description={buildRestaurantPageDescription(restaurant, {
          hasDelivery: !!guestOrdering?.guestOrdersOnline,
        })}
        path={`/restaurants/${appName}`}
        noIndex={!shouldIndexRestaurantPage(restaurant, menuItems)}
        images={[
          restaurant.coverImage
            ? getRestaurantImageUrl(restaurant.id, restaurant.coverImage)
            : undefined,
          restaurant.restaurantLandingDetailsList?.[0]?.image
            ? getRestaurantImageUrl(restaurant.id, restaurant.restaurantLandingDetailsList[0].image)
            : undefined,
          restaurant.appLogo ? getRestaurantImageUrl(restaurant.id, restaurant.appLogo) : undefined,
        ].filter((url): url is string => !!url)}
      />
      <Header />

      {/* Hero carousel with floating info */}
      <section className="rd-hero">
        <div className="rd-hero-slider">
          {heroSlides.map((src, index) => (
            <div
              key={`${src}-${index}`}
              className={`rd-hero-slide ${index === currentSlide ? 'active' : ''}`}
              role="img"
              aria-label={restaurant.restaurantName}
            >
              <div className="rd-hero-slide-bg" style={{ backgroundImage: `url(${src})` }} />
              <div className="rd-hero-slide-fit" style={{ backgroundImage: `url(${src})` }} />
            </div>
          ))}
          <div className="rd-hero-overlay" />
        </div>

        {heroSlides.length > 1 && (
          <div className="rd-hero-dots">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`rd-hero-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {renderHeroRatingButton('rd-hero-rating rd-hero-rating--desktop')}

        <div className="rd-hero-content">
          <div className="rd-hero-meta">
            {(isOpen !== null || guestOrdering?.guestOrdersOnline === false || (heroRating != null && heroRating > 0)) && (
              <div className="rd-hero-top-row">
                {(isOpen !== null || guestOrdering?.guestOrdersOnline === false) && (
                  <div className={`rd-hero-status ${guestOrdering?.guestOrdersOnline === false || !isOpen ? 'closed' : 'open'}`}>
                    <span className="rd-status-dot" />
                    {guestOrdering?.guestOrdersOnline === false ? 'Offline' : isOpen ? 'Open Now' : 'Closed Now'}
                  </div>
                )}
                {renderHeroRatingButton('rd-hero-rating rd-hero-rating--mobile')}
              </div>
            )}
            <h1 className="rd-hero-title">{restaurant.restaurantName}</h1>
            <p className="rd-hero-sub">
              <IconMapPin size={14} />
              <span>{getFullAddress()}</span>
            </p>
            {restaurant.cuisineList && restaurant.cuisineList.length > 0 && (
              <div className="rd-hero-tags">
                {restaurant.cuisineList.map((c) => (
                  <span key={c.id} className="rd-hero-tag">
                    {c.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="rd-hero-actions">
            <button type="button" className="rd-hero-btn rd-hero-btn--primary" onClick={handleBrowseMenu}>
              <IconMenuList size={16} />
              View Menu
            </button>
            <button type="button" className="rd-hero-btn rd-hero-btn--ghost" onClick={handleShare}>
              <IconShare size={16} />
              Share
            </button>
          </div>
        </div>
      </section>

      <div className="rd-ordering-notice">
        <GuestOrderingBanner status={guestOrdering} />
      </div>

      {/* Stats bar */}
      <div className="rd-stats-bar">
        <div className="rd-stats-bar-inner">
        {avgCostLabel && (
          <div className="rd-stat-item">
            <div className="rd-stat-icon rd-stat-icon--blue">
              <IconWallet size={18} />
            </div>
            <div>
              <div className="rd-stat-label">Avg. per pax</div>
              <div className="rd-stat-value">{avgCostLabel}</div>
            </div>
          </div>
        )}
        {offersCount > 0 && (
          <button type="button" className="rd-stat-item rd-stat-item--action" onClick={openOffersDrawer}>
            <div className="rd-stat-icon rd-stat-icon--green">
              <IconOffers size={18} />
            </div>
            <div>
              <div className="rd-stat-label">Active Offers</div>
              <div className="rd-stat-value">
                {offersCount} {offersCount === 1 ? 'Deal' : 'Deals'}
              </div>
            </div>
            <IconChevronRight size={14} className="rd-stat-chevron" aria-hidden />
          </button>
        )}
        {menuItems.length > 0 && (
          <div className="rd-stat-item">
            <div className="rd-stat-icon rd-stat-icon--red">
              <IconMenuItems size={18} />
            </div>
            <div>
              <div className="rd-stat-label">Menu Items</div>
              <div className="rd-stat-value">{menuItems.length} Items</div>
            </div>
          </div>
        )}
        {pepparAverageRating != null && pepparAverageRating > 0 && (
          <Link to={`/restaurants/${appName}/reviews`} className="rd-stat-item rd-stat-item--action">
            <div className="rd-stat-icon rd-stat-icon--amber">
              <i className="fa fa-star" aria-hidden />
            </div>
            <div>
              <div className="rd-stat-label">Peppar Rating</div>
              <div className="rd-stat-value">
                {pepparAverageRating.toFixed(1)}
                {pepparReviewCount > 0 ? ` (${pepparReviewCount})` : ''}
              </div>
            </div>
            <IconChevronRight size={14} className="rd-stat-chevron" aria-hidden />
          </Link>
        )}
        </div>
      </div>

      {catalogCuisines.length > 0 && (
        <div className="rd-catalog-section rd-catalog-section--cuisines">
          <div className="rd-catalog-section-inner">
            <CatalogCuisineSlider
              title="Explore Cuisines"
              items={catalogCuisines}
              onItemClick={handleBrowseCuisine}
            />
          </div>
        </div>
      )}

      {catalogCategories.length > 0 && (
        <div className="rd-catalog-section rd-catalog-section--categories">
          <div className="rd-catalog-section-inner">
            <CatalogCategoryGrid
              title="Explore Categories"
              items={catalogCategories}
              onItemClick={handleBrowseCategory}
            />
          </div>
        </div>
      )}

      {hasOrderModes && (
        <>
      <section className="rd-order-strip" aria-label="How to order">
        <div className="rd-order-strip-inner">{renderOrderModesCard()}</div>
      </section>
      <div ref={orderStripSentinelRef} className="rd-order-strip-sentinel" aria-hidden />

      <div
        className={`rd-order-sticky${orderSticky ? ' rd-order-sticky--visible' : ''}`}
        aria-hidden={!orderSticky}
        role="region"
        aria-label="Quick order options"
      >
        <div className="rd-order-sticky-inner">{renderOrderModeButtons(true)}</div>
      </div>

      <div
        className={`rd-order-sticky-spacer${orderSticky ? ' rd-order-sticky-spacer--active' : ''}`}
        aria-hidden
      />
        </>
      )}

      <div className="rd-body">
        <div className="rd-left-col">
          {showOnlinePresence && (
            <div className="rd-card">
              <div className="rd-card-header">
                <div className="rd-card-title">
                  <span className="rd-card-title-icon">
                    <IconShare size={16} />
                  </span>
                  Reviews &amp; Social
                </div>
              </div>
              <div className="rd-card-body">
                {platformReviewLinks.length > 0 && (
                  <div className="rd-platform-grid">
                    {platformReviewLinks.map((platform) => (
                      <a
                        key={platform.key}
                        href={platform.url}
                        className={`rd-platform-card ${platform.brandClass}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="rd-platform-name">{platform.label}</div>
                        {platform.rating != null && platform.rating > 0 ? (
                          <div className="rd-platform-rating">
                            <FooterRatingStars rating={platform.rating} className="rd-platform-stars" />
                            <span className="rd-platform-rating-val">{platform.rating.toFixed(1)}</span>
                            <span className="rd-platform-rating-max">/ 5</span>
                          </div>
                        ) : (
                          <div className="rd-platform-cta">View profile</div>
                        )}
                      </a>
                    ))}
                  </div>
                )}
                {socialMediaLinks.length > 0 && (
                  <div className={`rd-social-row${platformReviewLinks.length > 0 ? ' rd-social-row--spaced' : ''}`}>
                    {socialMediaLinks.map((link) => (
                      <a
                        key={link.key}
                        href={link.url}
                        className={`rd-social-btn rd-social-btn--${link.key}`}
                        title={link.label}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className={`fa fa-${link.icon}`} aria-hidden />
                        <span>{link.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="rd-card" id="customer-reviews" ref={customerReviewsRef}>
            <div className="rd-card-header">
              <div className="rd-card-title">
                <span className="rd-card-title-icon">
                  <i className="fa fa-star" aria-hidden />
                </span>
                Customer Reviews
              </div>
              <div className="rd-card-header-actions">
                {pepparReviewCount > 0 && (
                  <span className="rd-review-count-label">
                    {pepparReviewCount} review{pepparReviewCount !== 1 ? 's' : ''}
                  </span>
                )}
                <Link to={`/restaurants/${appName}/reviews`} className="rd-card-link-btn">
                  View all <IconChevronRight size={12} />
                </Link>
              </div>
            </div>
            <div className="rd-card-body">
              {pepparAverageRating != null && pepparAverageRating > 0 ? (
                <div className="cr-summary">
                  <FooterRatingStars rating={pepparAverageRating} className="cr-summary-stars" />
                  <span className="cr-summary-val">{pepparAverageRating.toFixed(1)}</span>
                  <span className="cr-summary-max">/ 5</span>
                  {displayListingRating != null &&
                    displayListingRating > pepparAverageRating && (
                      <span className="cr-summary-note">
                        Best overall {displayListingRating.toFixed(1)} including platform ratings
                      </span>
                    )}
                </div>
              ) : (
                <p className="cr-empty">No customer reviews yet.</p>
              )}

              <CustomerReviewList
                reviews={customerReviews}
                emptyMessage={
                  pepparAverageRating == null
                    ? 'Be the first to share your experience after dining here.'
                    : undefined
                }
              />

              {pepparReviewCount > HOME_REVIEWS_PREVIEW_LIMIT && (
                <div className="rd-review-view-all">
                  <Link to={`/restaurants/${appName}/reviews`} className="rd-review-cta-link">
                    See all {pepparReviewCount} reviews
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Loyalty Rewards — left column entry (shown when feature enabled + authenticated) */}
          {hasCustomerFeature(planFeatures, 'loyalty_rewards') && isAuthenticated && (
            <button
              type="button"
              onClick={() => navigate(`/restaurants/${appName}/rewards`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                textAlign: 'left',
                background: 'linear-gradient(135deg, #FFF8E1 0%, #FFF0EE 100%)',
                border: '1.5px solid #F5C21A',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 12,
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 26, lineHeight: 1 }}>🎁</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#2B2117' }}>My Rewards</div>
                  <div style={{ fontSize: 12, color: '#8B7355', marginTop: 2 }}>
                    Scratch cards &amp; milestone rewards
                  </div>
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#D9472B', whiteSpace: 'nowrap' }}>
                View →
              </span>
            </button>
          )}

          {/* Gallery */}
          {galleryImages.length > 0 && (
            <div className="rd-card">
              <div className="rd-card-header">
                <div className="rd-card-title">
                  <span className="rd-card-title-icon">
                    <IconCamera size={16} />
                  </span>
                  Photos &amp; Gallery
                </div>
                {galleryImages.length > 5 && (
                  <button type="button" className="rd-card-link-btn" onClick={handleBrowseMenu}>
                    +{galleryImages.length - 5} more
                  </button>
                )}
              </div>
              <div className="rd-card-body rd-gallery-body">
                <div className="rd-gallery-grid">
                  {galleryImages.slice(0, 5).map((src, idx) => (
                    <div key={src} className={`rd-gallery-item ${idx === 0 ? 'large' : ''}`}>
                      <img
                        src={src}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (el.src !== DEFAULT_IMAGES.MENU_ITEM) {
                            el.src = DEFAULT_IMAGES.MENU_ITEM;
                          } else {
                            el.style.visibility = 'hidden';
                          }
                        }}
                      />
                      {idx === 4 && galleryImages.length > 5 && (
                        <button
                          type="button"
                          className="rd-gallery-more"
                          onClick={handleBrowseMenu}
                          aria-label={`View ${galleryImages.length - 5} more photos on menu`}
                        >
                          +{galleryImages.length - 5}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <aside className="rd-right-col">
          {/* My Rewards — Loyalty Rewards Wallet entry point */}
          {hasCustomerFeature(planFeatures, 'loyalty_rewards') && isAuthenticated && (
            <button
              type="button"
              className="rd-card"
              onClick={() => navigate(`/restaurants/${appName}/rewards`)}
              style={{ width: '100%', textAlign: 'left', cursor: 'pointer', border: '1.5px solid #D9472B', background: '#FFF5F3', marginBottom: 8 }}
            >
              <div className="rd-card-header" style={{ paddingBottom: 0 }}>
                <div className="rd-card-title">
                  <span className="rd-card-title-icon">🎁</span>
                  My Rewards
                </div>
                <span style={{ fontSize: 12, color: '#D9472B', fontWeight: 600 }}>
                  View cards →
                </span>
              </div>
              <div className="rd-card-body" style={{ paddingTop: 6 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)' }}>
                  Scratch cards, stamp milestones, and spend rewards — all in one wallet.
                </p>
              </div>
            </button>
          )}

          {/* Play & Win — In-App Games entry point */}
          {hasCustomerFeature(planFeatures, 'in_app_games') && isAuthenticated && hasEnabledGames && (
            <button
              type="button"
              className="rd-card"
              onClick={() => navigate(`/restaurants/${appName}/games`)}
              style={{ width: '100%', textAlign: 'left', cursor: 'pointer', border: '1.5px solid var(--primary, #F5C21A)', background: 'var(--primary-light, #FFFBEA)' }}
            >
              <div className="rd-card-header" style={{ paddingBottom: 0 }}>
                <div className="rd-card-title">
                  <span className="rd-card-title-icon">🎮</span>
                  Play &amp; Win
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-secondary, #6B5E3E)', fontWeight: 600 }}>
                  Tap to play →
                </span>
              </div>
              <div className="rd-card-body" style={{ paddingTop: 6 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)' }}>
                  Spin the wheel, scratch a card, or roll the dice to win discounts on your meal!
                </p>
              </div>
            </button>
          )}

          {/* Offers & Deals */}
          {(promotions.length > 0 || publicCoupons.length > 0) && (
            <div className="rd-card">
              <div className="rd-card-header">
                <div className="rd-card-title">
                  <span className="rd-card-title-icon">
                    <IconGift size={16} />
                  </span>
                  Offers &amp; Deals
                </div>
                <button type="button" className="rd-card-link-btn" onClick={openOffersDrawer}>
                  View all <IconChevronRight size={12} />
                </button>
              </div>
              <div className="rd-card-body rd-offers-teaser-body">
                <p className="rd-offers-teaser-text">
                  {offersCount} active {offersCount === 1 ? 'deal' : 'deals'} — tap to see promotions and coupon codes.
                </p>
                <div className="rd-offers-list rd-offers-list--preview">
                  {promotions.slice(0, 1).map((p) => (
                    <div key={`p-${p.id}`} className="rd-offer-item">
                      <div className="rd-offer-badge">OFFER</div>
                      <div className="rd-offer-text">
                        <strong>{p.name}</strong>
                        {p.discountPreview ? ` - ${p.discountPreview}` : p.description ? ` - ${p.description}` : ''}
                      </div>
                    </div>
                  ))}
                  {promotions.length === 0 &&
                    publicCoupons.slice(0, 1).map((c) => (
                      <div key={`c-${c.id}`} className="rd-offer-item">
                        <div className="rd-offer-badge">{formatCouponBadge(c, currencySymbol)}</div>
                        <div className="rd-offer-text">
                          <strong>{c.couponCode || 'Coupon'}</strong> — {formatCouponText(c)}
                        </div>
                      </div>
                    ))}
                </div>
                <button type="button" className="rd-offers-open-btn" onClick={openOffersDrawer}>
                  View all deals
                </button>
              </div>
            </div>
          )}

          {/* Best Selling */}
          <div className="rd-card">
            <div className="rd-card-header">
              <div className="rd-card-title">
                <span className="rd-card-title-icon">
                  <IconMenuItems size={16} />
                </span>
                Best Selling Items
              </div>
              <button type="button" className="rd-card-link-btn" onClick={handleBrowseMenu}>
                Full menu <IconChevronRight size={12} />
              </button>
            </div>
            <div className="rd-card-body rd-specials-body">
              {renderPopularDishList(bestSellingItems)}
            </div>
          </div>

          {/* Today's Special */}
          <div className="rd-card">
            <div className="rd-card-header">
              <div className="rd-card-title">
                <span className="rd-card-title-icon">
                  <IconFlame size={16} />
                </span>
                Today&apos;s Special
              </div>
              <button type="button" className="rd-card-link-btn" onClick={handleBrowseMenu}>
                Full menu <IconChevronRight size={12} />
              </button>
            </div>
            <div className="rd-card-body rd-specials-body">
              {renderPopularDishList(todaysSpecials)}
            </div>
          </div>

          {/* Quick Info */}
          <div className="rd-card">
            <div className="rd-card-header">
              <div className="rd-card-title">
                <span className="rd-card-title-icon">
                  <IconPinCard size={16} />
                </span>
                Quick Info
              </div>
            </div>
            <div className="rd-card-body rd-quick-info-body">
              {restaurant.restaurantAddressDetails?.phone1 && (
                <div className="rd-info-row">
                  <span className="rd-info-icon">
                    <IconPhone size={16} />
                  </span>
                  <div>
                    <div className="rd-info-label">Phone Number</div>
                    <div className="rd-info-value">
                      <a href={`tel:${restaurant.restaurantAddressDetails.phone1}`}>
                        {restaurant.restaurantAddressDetails.phone1}
                      </a>
                    </div>
                  </div>
                </div>
              )}
              {restaurant.restaurantAddressDetails?.contactPerson && (
                <div className="rd-info-row">
                  <span className="rd-info-icon">
                    <IconUser size={16} />
                  </span>
                  <div>
                    <div className="rd-info-label">Contact Person</div>
                    <div className="rd-info-value">{restaurant.restaurantAddressDetails.contactPerson}</div>
                  </div>
                </div>
              )}
              {avgCostLabel && (
                <div className="rd-info-row">
                  <span className="rd-info-icon">
                    <IconWallet size={16} />
                  </span>
                  <div>
                    <div className="rd-info-label">Avg. per pax</div>
                    <div className="rd-info-value">{avgCostLabel}</div>
                  </div>
                </div>
              )}
              {establishedLabel && (
                <div className="rd-info-row">
                  <span className="rd-info-icon">
                    <IconCalendarSmall size={16} />
                  </span>
                  <div>
                    <div className="rd-info-label">Established</div>
                    <div className="rd-info-value">{establishedLabel}</div>
                  </div>
                </div>
              )}
              {restaurant.cuisineList && restaurant.cuisineList.length > 0 && (
                <div className="rd-info-row">
                  <span className="rd-info-icon">
                    <IconTag size={16} />
                  </span>
                  <div>
                    <div className="rd-info-label">Cuisine</div>
                    <div className="rd-info-value">
                      {restaurant.cuisineList.map((c) => c.name).join(' · ')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        <div className="rd-location-section">
          <div className="rd-card">
            <div className="rd-card-header">
              <div className="rd-card-title">
                <span className="rd-card-title-icon">
                  <IconMapPin size={16} />
                </span>
                Location
              </div>
            </div>
            <div className="rd-card-body">
              {showEmbeddedMap && restaurant.restaurantAddressDetails && (
                <div className="rd-map-wrap">
                  <iframe
                    title="Restaurant location"
                    src={`https://maps.google.com/maps?q=${restaurant.restaurantAddressDetails.lat},${restaurant.restaurantAddressDetails.lng}&hl=en&z=14&output=embed`}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}
              <p className="rd-address-text">{getFullAddress()}</p>
              {directionsUrl && (
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="rd-direction-btn">
                  <IconNavigation size={16} />
                  Get Directions
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer variant="restaurant" restaurant={restaurant} openingHours={openingHours} />

      <OffersDrawer
        open={offersDrawerOpen}
        onClose={() => setOffersDrawerOpen(false)}
        promotions={promotions}
        coupons={publicCoupons}
        formatCouponBadge={(coupon) => formatCouponBadge(coupon, currencySymbol)}
        formatCouponText={(coupon) => formatCouponText(coupon, formatPrice)}
      />
    </div>
  );
};

export default RestaurantHome;
